import { ThunkResult } from ".."
import { getUsers, handleUserData } from "../user/actions"
import { getPermissions, getUserPermissions } from "../permissions/actions"
import axios, { AxiosError } from "axios" //AxiosResponse
import { API_URL } from "../../api/Definitions"
import { iStringIndex } from "../../definitions/interfaces"
import { iExhibition } from "../../definitions/Exhibition"

export function handleInitialData(): ThunkResult<Promise<void>> {
  return (dispatch) =>
    new Promise((resolve) => {
      Promise.all([
        dispatch(handleUserData()),
        dispatch(getUserPermissions()),
        dispatch(getPermissions()),
        dispatch(getUsers()),
      ]).then(() => {
        resolve()
      })
    })
}

export function handleCollectionCount(collection = ""): Promise<number> {
  return new Promise((resolve) => {
    axios
      .get(`${API_URL}/v1/count/${collection}`)
      .then((result) => {
        resolve(result.data.count)
      })
      .catch((err) => {
        console.error(
          "Promise handleCollectionCount error catched: ",
          err.message,
        )
      })
  })
}

export interface iDataProps {
  link: string
  query?: unknown
}

interface iGetters {
  link: string
  query?: unknown
  fullUrl?: boolean
}

export function get<T>({ link, query, fullUrl = false }: iGetters): Promise<T> {
  return new Promise((resolve, reject) => {
    axios
      .get(fullUrl ? link : `${API_URL}${link}`, {
        params: query,
      })
      .then((res) => {
        resolve(res.data)
      })
      .catch((err: AxiosError) => {
        console.error("Promise error catched", err.message)
        reject(err.response)
      })
  })
}

export async function post<T>({
  link,
  data,
}: Partial<iUpdateProps<T>>): Promise<T> {
  const result = await axios.post(`${API_URL}${link}`, { data })
  return result.data
}

interface iDeleteProps<T> {
  data: Partial<T>
  fields: Partial<T>
  link: string
}

export async function handleDeleteData<T>({
  link,
}: Partial<iDeleteProps<T>>): Promise<T> {
  const result = await axios.delete(`${API_URL}${link}`)
  return result.data
}

interface iUpdateProps<T> {
  data: Partial<T>
  fields: Partial<T>
  link: string
}

export function handleUpdateData<T extends iStringIndex>({
  data,
  fields,
  link,
}: iUpdateProps<T>) {
  return new Promise((resolve, reject) => {
    const obj = dirtyValues(fields, data)

    console.log(obj)

    axios
      .post(`${API_URL}${link}`, { changedFields: obj, _id: data._id })
      .then((x) => {
        resolve(x.data)
      })
      .catch((err) => {
        reject(err.response.data)
      })
  })
}

interface iUpdateProps<T> {
  data: Partial<T>
  fields: Partial<T>
  link: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dirtyValues(dirtyFields: any | boolean, allValues: any): any {
  // If *any* item in an array was modified, the entire array must be submitted, because there's no way to indicate
  // "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues
  // Here, we have an object
  return Object.fromEntries(
    Object.keys(dirtyFields).map((key) => [
      key,
      dirtyValues(dirtyFields[key], allValues[key]),
    ]),
  )
}

interface iUploadProps {
  file: File
  fileCollection: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onProgress?: (e: any) => void
}

interface iUploadResponse {
  _id: string
}

export function handleUpload({
  file,
  fileCollection,
  onProgress,
}: iUploadProps): Promise<iUploadResponse> {
  return new Promise((resolve, reject) => {
    const f = new FileReader()
    f.onload = () => {
      axios
        .post(`${API_URL}/v1/upload`, f.result, {
          headers: {
            "x-filename": file.name,
            "x-file-collection": fileCollection,
            "content-type": file.type,
          },
          onUploadProgress: onProgress,
        })
        .then((res) => {
          resolve(res.data)
        })
        .catch(() => {
          reject()
        })
    }
    f.readAsArrayBuffer(file)
  })
}

interface iRemoveFile {
  _id: string
  fileCollection: string
}

export function handleRemoveFile({
  _id,
  fileCollection,
}: iRemoveFile): Promise<void> {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${API_URL}/v1/file`, {
        data: {
          _id,
          fileCollection,
        },
      })
      .then(() => {
        resolve()
      })
      .catch(reject)
  })
}

export function handleCreateExhibition(
  data: iExhibition,
): Promise<iExhibition> {
  return new Promise((resolve) => {
    axios.post(`${API_URL}/v2/create-exhibition`, { data }).then((result) => {
      resolve(result.data)
    })
  })
}
